import { isStringEqual } from "common";
import { SetOptional } from "type-fest";
import { Field, Form, PRECHECK_REQUIRED_FIELDS, PreCheckProductSelection, ProductObject } from "types";
import { api } from "@/api/api";
import { getSeller } from "@/utils/getSeller";

export type FieldPayload = SetOptional<Field, "id">;

/** Returns true if a form field matches a required precheck field by label. */
const fieldMatchesRequired = (field: Field, requiredField: FieldPayload): boolean => {
    return isStringEqual(field.label, requiredField.label);
};

const isPreCheckField = (field: Field): boolean => {
    return PRECHECK_REQUIRED_FIELDS.some((requiredField) => isStringEqual(field.label, requiredField.label));
};

export const hasAllPreCheckFields = (form: Form): boolean => {
    return PRECHECK_REQUIRED_FIELDS.every((requiredField) =>
        form.fields.some((field) => fieldMatchesRequired(field, requiredField)),
    );
};

export const getMissingPreCheckFields = (form: Form): FieldPayload[] => {
    return PRECHECK_REQUIRED_FIELDS.filter(
        (requiredField) => !form.fields.some((field) => fieldMatchesRequired(field, requiredField)),
    );
};

/**
 * Merges existing form fields with missing required fields.
 * Assigns sequence numbers to new fields continuing from the highest existing sequence.
 */
export const mergeFormFields = (existingFields: Field[], missingFields: FieldPayload[]): FieldPayload[] => {
    const maxSeq = existingFields.length > 0 ? Math.max(...existingFields.map((f) => f.seq ?? 0)) : 0;
    return [...existingFields, ...missingFields.map((field, index) => ({ ...field, seq: maxSeq + index + 1 }))];
};

export const getNonPreCheckFields = (form: Form): Field[] => {
    return form.fields.filter((field) => !isPreCheckField(field));
};

/** Get form for a specific product by ID and type. */
export const getFormForProduct = (
    forms: Form[],
    productId: string,
    productType: ProductObject = ProductObject.EXPERIENCE,
): Form | undefined => {
    return forms.find((form) => form.product?.id === productId && form.productType === productType);
};

/**
 * Get all products (with type information) that have precheck forms.
 * Returns PreCheckProductSelection[] with both id and object type.
 */
export const getProductsWithPreCheckForms = (forms: Form[]): PreCheckProductSelection[] => {
    return forms
        .filter((form) => form.product?.id && hasAllPreCheckFields(form))
        .map((form) => ({ id: form.product!.id, object: form.productType }));
};

/** Create payload for a new precheck form. Field IDs are omitted — the server will generate them. */
export const createPreCheckFormPayload = (
    productId: string,
    productType: ProductObject = ProductObject.EXPERIENCE,
): Omit<Form, "id" | "fields"> & { fields: FieldPayload[] } => {
    return {
        fields: PRECHECK_REQUIRED_FIELDS,
        organizerOnly: false,
        seller: { id: getSeller().id },
        productType,
        product: { id: productId },
    };
};

/** Create payload for updating an existing form. */
export const createUpdateFormPayload = (
    existingForm: Form,
    productId: string,
    fields: FieldPayload[],
    productType: ProductObject = ProductObject.EXPERIENCE,
): Omit<Form, "fields"> & { fields: FieldPayload[] } => {
    return {
        id: existingForm.id,
        organizerOnly: existingForm.organizerOnly,
        seller: { id: getSeller().id },
        product: { id: productId },
        productType,
        fields,
    };
};

const getProductKey = (product: PreCheckProductSelection) => `${product.object}:${product.id}`;

export const managePreCheckForms = async (
    forms: Form[],
    previouslySavedProducts: PreCheckProductSelection[],
    selectedProducts: PreCheckProductSelection[],
): Promise<void> => {
    const selectedKeys = new Set(selectedProducts.map((p) => getProductKey(p)));
    const previousKeys = new Set(previouslySavedProducts.map((p) => getProductKey(p)));

    const productsToAdd = selectedProducts.filter((product) => !previousKeys.has(getProductKey(product)));
    const productsToRemove = previouslySavedProducts.filter((product) => !selectedKeys.has(getProductKey(product)));

    const addResults = await Promise.allSettled(
        productsToAdd.map(async (product) => {
            const existingForm = getFormForProduct(forms, product.id, product.object);
            if (!existingForm) {
                const payload = createPreCheckFormPayload(product.id, product.object);
                await api.post("/forms", payload);
            } else if (!hasAllPreCheckFields(existingForm)) {
                const missingFields = getMissingPreCheckFields(existingForm);
                const updatedFields = mergeFormFields(existingForm.fields, missingFields);
                const payload = createUpdateFormPayload(existingForm, product.id, updatedFields, product.object);
                await api.put(`/forms/${existingForm.id}`, payload);
            }
        }),
    );

    const removeResults = await Promise.allSettled(
        productsToRemove.map(async (product) => {
            const existingForm = getFormForProduct(forms, product.id, product.object);
            if (existingForm) {
                const fieldsToKeep = getNonPreCheckFields(existingForm);
                if (fieldsToKeep.length > 0) {
                    const payload = createUpdateFormPayload(existingForm, product.id, fieldsToKeep, product.object);
                    await api.put(`/forms/${existingForm.id}`, payload);
                } else {
                    await api.delete(`/forms/${existingForm.id}`);
                }
            }
        }),
    );

    const failures = [...addResults, ...removeResults].filter((r) => r.status === "rejected");
    if (failures.length > 0) {
        throw new Error(`${failures.length} form operation(s) failed`);
    }
};
